import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Mail, Sparkles, Loader2, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, pdfUrl } from "@/lib/api";

export default function AdminPdfPreview() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pages, setPages] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  // Bust the image cache when admin asks to regenerate.
  const [cacheKey, setCacheKey] = useState(() => Date.now());

  useEffect(() => {
    api.get(`/admin/projects/${id}`).then((r) => {
      setProject(r.data);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    setPages(null);
    setPreviewError(false);
    api.get(`/admin/projects/${id}/pdf-preview`)
      .then((r) => setPages(r.data.pages))
      .catch(() => setPreviewError(true));
  }, [id, cacheKey]);

  const openInNewTab = () => {
    window.open(pdfUrl(`/admin/projects/${id}/pdf`), "_blank", "noopener");
  };

  const resend = async () => {
    setBusy("resend");
    try {
      await api.post(`/admin/projects/${id}/resend-pdf`);
      toast.success("PDF email queued");
    } finally {
      setBusy("");
    }
  };

  const regenerate = async () => {
    setPages(null);
    setPreviewError(false);
    try {
      const { data } = await api.get(`/admin/projects/${id}/pdf-preview`, { params: { refresh: 1 } });
      setPages(data.pages);
      setCacheKey(Date.now());
      toast.success("PDF refreshed");
    } catch {
      setPreviewError(true);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to={`/admin/projects/${id}`} className="rcw-btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to project
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={regenerate} className="rcw-btn-ghost text-sm" data-testid="pdf-preview-refresh">
              <Sparkles className="h-4 w-4" /> Refresh
            </button>
            <button onClick={openInNewTab} className="rcw-btn-ghost text-sm" data-testid="pdf-preview-download">
              <Download className="h-4 w-4" /> Open / Download
            </button>
            <button onClick={resend} disabled={!!busy} className="rcw-btn-amber text-sm" data-testid="pdf-preview-resend">
              {busy === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email PDF
            </button>
          </div>
        </div>

        <div className="rcw-label mt-4">PDF preview</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1 text-gray-900">
          {project?.project_name || "Project PDF"}
        </h1>
        {project ? (
          <div className="text-sm text-gray-500 mt-1">
            {project.client_name} · {project.location} · {project.status}
          </div>
        ) : null}

        <div className="mt-5 rcw-card overflow-hidden" data-testid="pdf-preview-frame">
          {loading || pages === null ? (
            previewError ? (
              <div className="h-[40vh] flex flex-col items-center justify-center text-gray-500 gap-3">
                <div>Couldn't render the preview.</div>
                <button onClick={openInNewTab} className="rcw-btn-amber text-sm">
                  <Download className="h-4 w-4" /> Open / Download the PDF instead
                </button>
              </div>
            ) : (
              <div className="h-[80vh] flex items-center justify-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building PDF preview…
              </div>
            )
          ) : (
            <div className="bg-gray-200 p-3 sm:p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {Array.from({ length: pages }, (_, i) => (
                <img
                  key={i}
                  src={pdfUrl(`/admin/projects/${id}/pdf-preview/${i + 1}`, { v: cacheKey })}
                  alt={`Page ${i + 1}`}
                  className="w-full max-w-3xl mx-auto shadow-md rounded-sm bg-white"
                  data-testid={`pdf-preview-page-${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-xs text-gray-500 mt-3 flex items-center gap-1">
          <Maximize2 className="h-3 w-3" /> Tip: click <b>Open / Download</b> for full-screen.
        </div>
      </div>
    </Layout>
  );
}
